import type { Href } from "expo-router";
import { useRouter } from "expo-router";
import { useEffect, useState, type ReactNode } from "react";
import { Platform, Pressable, Text, useWindowDimensions, View } from "react-native";
import { COLORS, FONTS, RADII, SPACING, STROKES } from "../../lib/brand";
import { PANE_THEMES } from "../brand/paneThemes";
import Svg, { Circle, Path, Rect } from "react-native-svg";

import { StudentPortalShell, type StudentPortalIdentity } from "./StudentPortalShell";
import { BrandBackdrop } from "./BrandBackdrop";

const SIDEBAR_W = 220;

const STUDENT_THEME = PANE_THEMES.Student;

export type ProgramTab = "dashboard" | "deliverables" | "team" | "info";

const TABS: { key: ProgramTab; label: string; teamLabel?: string }[] = [
  { key: "dashboard", label: "Dashboard" },
  { key: "deliverables", label: "Deliverables" },
  { key: "team", label: "Find Team", teamLabel: "Team" },
  { key: "info", label: "Competition Info" },
];

export type StudentProgramShellProps = {
  /** Competition / program name shown in the persistent header. */
  topbarTitle: string;
  topbarFirstName: string;
  avatarName: string;
  avatarUrl?: string | null;
  identity?: StudentPortalIdentity | null;
  casesBadgeCount?: number;

  /** Currently active tab in the green sidebar. */
  activeTab: ProgramTab;
  /** Triggered when the user picks a tab. */
  onTabChange: (tab: ProgramTab) => void;
  /** True once the student is on a team — flips "Find Team" → "Team". */
  hasJoinedTeam: boolean;
  /** Unread team-chat count — drives a small dot/badge on the "Team" sidebar tab. */
  teamUnreadCount?: number;

  /**
   * Optional competition id. When provided, the slide-in animation only
   * runs the first time the student enters this competition during their
   * tab session — subsequent navigations between the workspace and the
   * team page (same competition) skip the animation, so it feels like
   * one continuous workspace.
   */
  competitionId?: string | null;

  /** Children render inside the right-hand body area. The body is NOT
   * wrapped in a ScrollView — children should bring their own. */
  children: ReactNode;
};

/**
 * Wraps the persistent student header (StudentPortalShell with the default
 * green sidebar hidden) and adds a program-scoped forest sidebar that
 * slides in from the left on mount with a 280ms transition. The sidebar
 * holds the four program tabs (Dashboard / Deliverables / Find Team or
 * Team / Competition Info) plus a back arrow that returns the student
 * to the top-level dashboard.
 *
 * Each tab is callback-driven; the workspace owns its own active-tab
 * state and decides what to render in the body.
 */
export function StudentProgramShell({
  topbarTitle,
  topbarFirstName,
  avatarName,
  avatarUrl = null,
  identity = null,
  casesBadgeCount = 0,
  activeTab,
  onTabChange,
  hasJoinedTeam,
  teamUnreadCount = 0,
  competitionId = null,
  children,
}: StudentProgramShellProps) {
  const router = useRouter();
  const { width } = useWindowDimensions();
  const wide = width >= 900;

  // Slide-in animation. Runs once per (tab session, competition) so
  // navigating between the workspace and the team page within the same
  // program does not retrigger the animation.
  const animKey =
    competitionId
      ? `coravu.programEntered.${competitionId}`
      : null;
  const alreadyEntered = (() => {
    if (Platform.OS !== "web" || !animKey) return false;
    try {
      return typeof window !== "undefined" && window.sessionStorage?.getItem(animKey) === "1";
    } catch {
      return false;
    }
  })();
  const [slidIn, setSlidIn] = useState(alreadyEntered);
  useEffect(() => {
    if (alreadyEntered) {
      setSlidIn(true);
      return;
    }
    if (Platform.OS !== "web") {
      setSlidIn(true);
      return;
    }
    const id = requestAnimationFrame(() => {
      setSlidIn(true);
      try {
        if (animKey && typeof window !== "undefined") {
          window.sessionStorage?.setItem(animKey, "1");
        }
      } catch {
        // sessionStorage unavailable — animation just runs every time, fine.
      }
    });
    return () => cancelAnimationFrame(id);
  }, [alreadyEntered, animKey]);

  return (
    <StudentPortalShell
      topbarTitle={topbarTitle}
      topbarFirstName={topbarFirstName}
      avatarName={avatarName}
      avatarUrl={avatarUrl}
      identity={identity}
      casesBadgeCount={casesBadgeCount}
      hideSidebar
    >
      <View style={{ flex: 1, flexDirection: wide ? "row" : "column", backgroundColor: COLORS.SAND.hex }}>
        {/* Green program sidebar — slides in on mount. */}
        {wide ? (
          <View
            style={[
              {
                width: SIDEBAR_W,
                backgroundColor: COLORS.FOREST.hex,
                borderRightWidth: STROKES.hairline,
                borderRightColor: STUDENT_THEME.accentSoft,
                paddingVertical: 18,
                transform: [{ translateX: slidIn ? 0 : -SIDEBAR_W }],
              },
              Platform.OS === "web"
                ? ({
                    transition: "transform 0.32s cubic-bezier(0.4, 0, 0.2, 1)",
                  } as object)
                : null,
            ]}
          >
            <BackToDashboard onPress={() => router.push("/student" as Href)} />

            {/* Active-case overline. Pulls the competition name forward as
                a small, faint label so the sidebar reads as "the workspace
                of <case>" — same idiom the client portal uses for its case
                switcher. Hidden when there's no title (e.g., a 404 / empty
                state) so the sidebar doesn't render an orphan blank line. */}
            {topbarTitle && topbarTitle.trim().length > 0 ? (
              <View
                style={{
                  marginTop: 22,
                  marginBottom: 6,
                  paddingHorizontal: 22,
                }}
              >
                <Text
                  numberOfLines={3}
                  style={{
                    fontFamily: FONTS.bodyBold,
                    fontSize: 10,
                    letterSpacing: 1.8,
                    textTransform: "uppercase",
                    color: STUDENT_THEME.textOnSidebarIdle,
                    lineHeight: 15,
                  }}
                >
                  {topbarTitle}
                </Text>
              </View>
            ) : null}

            <View style={{ marginTop: 12, gap: 1 }}>
              {TABS.map((t) => {
                const label = t.key === "team" && hasJoinedTeam ? t.teamLabel ?? t.label : t.label;
                const showBadge =
                  t.key === "team" && teamUnreadCount > 0 && activeTab !== "team";
                return (
                  <SidebarTab
                    key={t.key}
                    label={label}
                    active={activeTab === t.key}
                    onPress={() => onTabChange(t.key)}
                    icon={t.key}
                    badgeCount={showBadge ? teamUnreadCount : null}
                  />
                );
              })}
            </View>
          </View>
        ) : (
          /* Narrow viewport: tabs render as a horizontal pill bar at the top. */
          <View
            style={{
              backgroundColor: COLORS.FOREST.hex,
              paddingHorizontal: 14,
              paddingVertical: 10,
              flexDirection: "row",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
              borderBottomWidth: STROKES.hairline,
              borderBottomColor: STUDENT_THEME.accentSoft,
            }}
          >
            <Pressable
              onPress={() => router.push("/student" as Href)}
              accessibilityRole="button"
              accessibilityLabel="Back to home"
              style={[
                {
                  width: 28,
                  height: 28,
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: RADII.xs,
                  backgroundColor: STUDENT_THEME.surfaceDividerFaint,
                },
                Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
              ]}
            >
              <Svg width={11} height={11} viewBox="0 0 10 10" fill="none">
                <Path
                  d="M8 5H2M4.5 2.5L2 5l2.5 2.5"
                  stroke={COLORS.LINEN.hex}
                  strokeWidth={1.5}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </Svg>
            </Pressable>
            {TABS.map((t) => {
              const label = t.key === "team" && hasJoinedTeam ? t.teamLabel ?? t.label : t.label;
              return (
                <Pressable
                  key={t.key}
                  onPress={() => onTabChange(t.key)}
                  style={[
                    {
                      paddingHorizontal: 10,
                      paddingVertical: 6,
                      borderRadius: RADII.xs,
                      backgroundColor:
                        activeTab === t.key ? STUDENT_THEME.sidebarItemActiveBg : "transparent",
                    },
                    Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                  ]}
                >
                  <Text
                    style={{
                      fontFamily:
                        activeTab === t.key ? FONTS.bodySemiBold : FONTS.bodyMedium,
                      fontSize: 11,
                      letterSpacing: 0.6,
                      color: activeTab === t.key ? COLORS.LINEN.hex : STUDENT_THEME.textOnSidebarDim,
                    }}
                  >
                    {label}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        )}

        {/* Right-hand body — children render their own scroll. The brand
            backdrop sits absolute behind them so the per-program surfaces
            (Dashboard / Deliverables / Team / Competition Info) feel of a
            piece with the marketing landing instead of plain flat sand. */}
        <View style={{ flex: 1, minWidth: 0, backgroundColor: COLORS.SAND.hex }}>
          <BrandBackdrop tone="sand" />
          <View style={{ flex: 1, zIndex: 1 }}>{children}</View>
        </View>
      </View>
    </StudentPortalShell>
  );
}

function BackToDashboard({ onPress }: { onPress: () => void }) {
  const isWeb = Platform.OS === "web";
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel="Back to home"
      style={({ hovered }) => [
        {
          flexDirection: "row",
          alignItems: "center",
          gap: 8,
          paddingVertical: SPACING.xs,
          paddingHorizontal: 18,
          marginHorizontal: 4,
          borderRadius: RADII.xs,
          backgroundColor: "transparent",
        },
        isWeb && hovered ? { backgroundColor: STUDENT_THEME.surfaceDividerFaint } : null,
        isWeb ? ({ cursor: "pointer" } as const) : null,
      ]}
    >
      <Svg width={11} height={11} viewBox="0 0 10 10" fill="none">
        <Path
          d="M8 5H2M4.5 2.5L2 5l2.5 2.5"
          stroke={COLORS.AMBER.hex}
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </Svg>
      <Text
        style={{
          fontFamily: FONTS.bodySemiBold,
          fontSize: 10.5,
          letterSpacing: 1.4,
          textTransform: "uppercase",
          color: COLORS.AMBER.hex,
        }}
      >
        Back to home
      </Text>
    </Pressable>
  );
}

function SidebarTab({
  label,
  active,
  onPress,
  icon,
  badgeCount = null,
}: {
  label: string;
  active: boolean;
  onPress: () => void;
  icon: ProgramTab;
  badgeCount?: number | null;
}) {
  const isWeb = Platform.OS === "web";
  const tint = active ? COLORS.AMBER.hex : COLORS.SAGE.hex;
  return (
    <Pressable
      onPress={onPress}
      style={({ hovered }) => [
        {
          flexDirection: "row",
          alignItems: "center",
          gap: 10,
          paddingVertical: 10,
          paddingHorizontal: 20,
          borderLeftWidth: STROKES.strong,
          borderLeftColor: active ? COLORS.AMBER.hex : "transparent",
          backgroundColor: active ? STUDENT_THEME.accentTintSoft : "transparent",
        },
        isWeb && hovered && !active
          ? { backgroundColor: STUDENT_THEME.neutralHoverBgOnDark }
          : null,
        isWeb
          ? ({
              cursor: "pointer",
              transition: "background-color 0.15s ease",
            } as const)
          : null,
      ]}
    >
      <View style={{ opacity: active ? 1 : 0.55 }}>
        <ProgramTabIcon kind={icon} color={tint} />
      </View>
      <Text
        style={{
          flex: 1,
          fontFamily: FONTS.bodyMedium,
          fontSize: 12.2,
          color: active ? COLORS.LINEN.hex : STUDENT_THEME.textOnSidebarDim,
        }}
      >
        {label}
      </Text>
      {badgeCount != null && badgeCount > 0 ? (
        <View
          style={{
            paddingHorizontal: 6,
            paddingVertical: 2,
            borderRadius: RADII.full,
            backgroundColor: COLORS.AMBER.hex,
            minWidth: 18,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 9,
              color: COLORS.FOREST.hex,
              letterSpacing: 0.3,
            }}
          >
            {badgeCount > 99 ? "99+" : String(badgeCount)}
          </Text>
        </View>
      ) : null}
    </Pressable>
  );
}

function ProgramTabIcon({ kind, color }: { kind: ProgramTab; color: string }) {
  if (kind === "dashboard") {
    return (
      <Svg width={14} height={14} viewBox="0 0 14 14" fill="none">
        <Rect x={1} y={1} width={5} height={5} rx={1} stroke={color} strokeWidth={1.1} />
        <Rect x={8} y={1} width={5} height={5} rx={1} stroke={color} strokeWidth={1.1} />
        <Rect x={1} y={8} width={5} height={5} rx={1} stroke={color} strokeWidth={1.1} />
        <Rect x={8} y={8} width={5} height={5} rx={1} stroke={color} strokeWidth={1.1} />
      </Svg>
    );
  }
  if (kind === "deliverables") {
    return (
      <Svg width={14} height={14} viewBox="0 0 14 14" fill="none">
        <Path
          d="M3 1.5h6l3 3v8a1 1 0 01-1 1H3a1 1 0 01-1-1v-10a1 1 0 011-1z"
          stroke={color}
          strokeWidth={1.1}
          strokeLinejoin="round"
        />
        <Path d="M9 1.5v3h3" stroke={color} strokeWidth={1.1} strokeLinejoin="round" />
        <Path d="M4.5 7.5h5M4.5 9.5h5M4.5 11.5h3" stroke={color} strokeWidth={1.1} strokeLinecap="round" />
      </Svg>
    );
  }
  if (kind === "team") {
    return (
      <Svg width={14} height={14} viewBox="0 0 14 14" fill="none">
        <Circle cx={5} cy={4} r={2.2} stroke={color} strokeWidth={1.1} />
        <Circle cx={10} cy={5} r={1.8} stroke={color} strokeWidth={1.1} />
        <Path d="M1 12c0-2.2 1.8-4 4-4s4 1.8 4 4" stroke={color} strokeWidth={1.1} strokeLinecap="round" />
        <Path d="M10 8c1.7 0 3 1.1 3 2.5" stroke={color} strokeWidth={1.1} strokeLinecap="round" />
      </Svg>
    );
  }
  // info
  return (
    <Svg width={14} height={14} viewBox="0 0 14 14" fill="none">
      <Circle cx={7} cy={7} r={5.5} stroke={color} strokeWidth={1.1} />
      <Path d="M7 6.2v3.5" stroke={color} strokeWidth={1.4} strokeLinecap="round" />
      <Circle cx={7} cy={4.4} r={0.7} fill={color} />
    </Svg>
  );
}
